import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { normalizeLanguage, supportedLanguages, translations, SupportedLanguage } from './i18n';
import { DEV_USER_ID_STORAGE_KEY } from './services/api';
import { platformApi } from './services/platformApi';

interface LanguageContextValue {
  language: SupportedLanguage;
  direction: 'ltr' | 'rtl';
  locale: string;
  setLanguage: (language: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const initialLanguage = normalizeLanguage(process.env.REACT_APP_DEFAULT_UI_LANGUAGE || 'en');

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(initialLanguage);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  const setLanguage = useCallback((nextLanguage: string) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguageState(normalized);
    if (currentUserId) {
      platformApi.updateCurrentUserPreferences(normalized).catch(() => {
        setLanguageState(language);
      });
      return;
    }
    window.localStorage.setItem('talegence-ui-language', normalized);
  }, [currentUserId, language]);

  const loadUserLanguage = useCallback(() => {
    const hasUserIdentity =
      Boolean(window.localStorage.getItem(DEV_USER_ID_STORAGE_KEY)) ||
      Boolean(process.env.REACT_APP_DEV_USER_ID) ||
      Boolean(window.localStorage.getItem('authToken'));

    if (!hasUserIdentity) {
      const storedLanguage = window.localStorage.getItem('talegence-ui-language');
      if (storedLanguage) {
        setLanguageState(normalizeLanguage(storedLanguage));
      }
      return;
    }

    return platformApi
      .getCurrentUser()
      .then((user) => {
        setCurrentUserId(user.id);
        setLanguageState(normalizeLanguage(user.preferred_ui_language));
      })
      .catch(() => {
        setCurrentUserId(null);
      });
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      const result = loadUserLanguage();
      if (result && 'then' in result) {
        result.catch(() => {
          if (mounted) {
            setCurrentUserId(null);
          }
        });
      }
    };
    load();
    window.addEventListener('talegence-auth-changed', load);

    return () => {
      mounted = false;
      window.removeEventListener('talegence-auth-changed', load);
    };
  }, [loadUserLanguage]);

  const value = useMemo(() => {
    const metadata = supportedLanguages[language];
    return {
      language,
      direction: metadata.direction,
      locale: metadata.locale,
      setLanguage,
      t: (key: string) => translations[language][key] || translations.en[key] || key,
    };
  }, [language, setLanguage]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = value.direction;
  }, [language, value.direction]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return context;
};
